<template>
    <div class="flex h-screen bg-gray-50">
      <!-- Sidebar -->
      <SideBar class="flex-shrink-0" />
      
      <!-- Main content area -->
      <div class="flex-1 overflow-auto p-4 md:p-6">
        <div class="max-w-4xl mx-auto">
          <!-- Header with title and navigation -->
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
            <div class="flex items-center gap-3">
              <h1 class="text-2xl font-bold text-gray-900">Cold Calling</h1>
              <div
                v-if="currentLead"
                class="inline-flex items-center justify-center w-8 h-8 rounded-full text-black text-sm font-bold"
                :style="{ backgroundColor: getAvatarColor(currentLead.user_id) }"
                :title="$store.getters['leads/usernameById'](currentLead.user_id)"
              >
                {{ getInitials($store.getters['leads/usernameById'](currentLead.user_id)) }}
              </div>
            </div>
          </div>
            <div class="flex items-center gap-3">
              <div class="text-sm text-gray-600">
                Lead {{ currentIndex + 1 }} of {{ filteredLeads.length }}
              </div>
              <div class="flex gap-2">
                <button 
                  @click="previousLead" 
                  :disabled="currentIndex === 0"
                  class="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                </button>
                <button 
                  @click="nextLead" 
                  :disabled="currentIndex >= filteredLeads.length - 1"
                  class="p-2 rounded-lg bg-primary text-black hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Filter options -->
          <div class="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div class="flex flex-wrap gap-3">
              <button 
                @click="activeFilter = ''" 
                :class="[
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  activeFilter === '' 
                    ? 'bg-primary text-black border-primary' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                ]"
              >
                All Leads
              </button>
              <button 
                @click="activeFilter = 'smart_sort'" 
                :class="[
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  activeFilter === 'smart_sort' 
                    ? 'bg-primary text-black border-primary' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                ]"
              >
                Smart Sort
            </button>
              <button 
                @click="activeFilter = 'follow_up_today'" 
                :class="[
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  activeFilter === 'follow_up_today' 
                    ? 'bg-primary text-black border-primary' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                ]"
              >
                <span class="flex items-center gap-1">
                  <span class="h-2 w-2 rounded-full bg-red-500"></span>
                  Follow-up Today
                </span>
              </button>
              <button 
                @click="activeFilter = 'new'" 
                :class="[
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  activeFilter === 'new' 
                    ? 'bg-primary text-black border-primary' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                ]"
              >
                New Leads
              </button>
              <select 
                v-model="stateFilter" 
                class="px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
              >
                <option value="">All States</option>
                <option v-for="state in availableStates" :value="state" :key="state">{{ state }}</option>
              </select>
            </div>
          </div>
          
          <!-- Lead card -->
          <div v-if="currentLead" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <!-- Lead header -->
            <div class="p-6 border-b border-gray-200">
              <div class="flex items-start w-full">
                <div class="flex items-center gap-4 flex-none max-w-[80%]">
                  <div class="h-16 w-16 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden">
                    <img 
                      v-if="currentLead.meta && JSON.parse(currentLead.meta).thumbnail"
                      :src="JSON.parse(currentLead.meta).thumbnail" 
                      alt="Lead" 
                      class="h-full w-full object-cover"
                    />
                    <div v-else class="h-full w-full flex items-center justify-center text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"></circle><path d="M20 21a8 8 0 0 0-16 0"></path></svg>
                    </div>
                  </div>
                  <div class="max-w-[55%]">
                    <h2 class="text-xl font-bold text-gray-900">{{ currentLead.name }}</h2>
                    <p class="text-gray-500">{{ currentLead.company || 'No company' }}</p>
                  </div>
                  <div v-if="parseHours(currentLead.meta)" class="mt-1 text-sm">
                    <div 
                      v-for="(hours, day) in parseHours(currentLead.meta)" 
                      :key="day" 
                      :class="isOpenNowFancy(hours, day) ? 'text-green-600 font-semibold' : 'text-gray-500'">
                      {{ day }} - {{ hours }}
                    </div>
                  </div>
                </div>
                <div class="flex justify-end gap-2 flex-1">
                  <div 
                    :class="[
                      'px-3 py-1 text-sm font-medium rounded-full',
                      getStatusClass(currentLead.status)
                    ]"
                  >
                    {{ currentLead.status }}
                  </div>
                  <button 
                    @click="openEditModal" 
                    class="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                    title="Edit lead"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path></svg>
                  </button>
                  <svg @click.stop="openEmailSlider(lead)" class="cursor-pointer ml-2" style="width: 25px;" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 7.00005L10.2 11.65C11.2667 12.45 12.7333 12.45 13.8 11.65L20 7" stroke="#5c5c5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="#5c5c5c" strokeWidth="2" strokeLinecap="round"/>
    </svg>
                </div>
              </div>
            </div>
            
            <!-- Lead details -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b border-gray-200">
              <div>
                <h3 class="text-sm font-medium text-gray-500 mb-4">CONTACT DETAILS</h3>
                <div class="space-y-3">
                  <div class="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                    <div class="flex items-center gap-2">
                      <span class="text-gray-900">{{ currentLead.phone || 'No phone' }}</span>
                      <button 
                        @click="copyPhone(currentLead.phone)" 
                        class="text-xs text-primary hover:text-primary/80"
                      >
                        {{ phoneIsCopied ? 'Copied!' : 'Copy' }}
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
                    <span class="text-gray-900">{{ currentLead.email || 'No email' }}</span>
                  </div>
                  <div class="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                    <div class="text-gray-900">
                      <div>{{ currentLead.street || 'No address' }}</div>
                      <div>{{ formatCityStateZip(currentLead) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 class="text-sm font-medium text-gray-500 mb-4">FOLLOW-UP</h3>
                <div v-if="!isEditingFollowUp">
                  <div class="flex justify-between items-center mb-4">
                    <div>
                      <div v-if="currentLead.follow_up_date" class="flex items-center gap-2">
                        <span 
                          class="h-3 w-3 rounded-full" 
                          :class="[
                            isOverdue(currentLead.follow_up_date) ? 'bg-red-600' : 
                            isToday(currentLead.follow_up_date) ? 'bg-red-500' :
                            isUpcoming(currentLead.follow_up_date) ? 'bg-amber-400' :
                            'bg-green-500'
                          ]"
                        ></span>
                        <span 
                          class="text-sm font-medium"
                          :class="[
                            isOverdue(currentLead.follow_up_date) ? 'text-red-700' : 
                            isToday(currentLead.follow_up_date) ? 'text-red-600' :
                            isUpcoming(currentLead.follow_up_date) ? 'text-amber-700' :
                            'text-green-700'
                          ]"
                        >
                          {{ formatFollowUpDateLong(currentLead.follow_up_date) }}
                        </span>
                      </div>
                      <div v-else class="text-gray-500 text-sm">No follow-up scheduled</div>
                    </div>
                    <div class="flex gap-2">
                      <button 
                        @click="startEditFollowUp" 
                        class="text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                      >
                        {{ currentLead.follow_up_date ? 'Edit' : 'Set Date' }}
                      </button>
                      <button 
                        v-if="currentLead.follow_up_date"
                        @click="clearFollowUpDate" 
                        class="text-sm text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
                
                <div v-else class="space-y-3 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Select follow-up date</label>
                    <input 
                      type="datetime-local" 
                      v-model="followUpDateDraft"
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div class="flex justify-end gap-2">
                    <button 
                      @click="cancelEditFollowUp" 
                      class="px-3 py-1.5 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button 
                      @click="saveFollowUpDate" 
                      class="px-3 py-1.5 text-sm bg-primary text-black rounded-md hover:bg-primary/90"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Quick actions -->
            <div class="p-6 border-b border-gray-200">
              <h3 class="text-sm font-medium text-gray-500 mb-4">QUICK ACTIONS</h3>
              <div class="flex flex-wrap gap-2">
                <button 
                  v-for="status in statusesList" 
                  :key="status"
                  @click="updateLeadStatus(status)"
                  :class="[
                    'px-3 py-2 text-sm rounded-md border transition-colors',
                    currentLead.status === status 
                      ? 'bg-primary text-black border-primary' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  ]"
                >
                  {{ status }}
                </button>
              </div>
              
              <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button 
                  @click="updateLeadAction('Inactive','Not Interested')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m4.93 4.93 14.14 14.14"></path></svg>
                  <span>Not Interested</span>
                </button>
                <button 
                  @click="updateLeadAction('Inactive', 'Wrong Number/Disconnected')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                <svg style="width: 20px" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M22.7399 6.32717C24.3781 8.48282 24.2132 11.571 22.2453 13.5389L20.3007 15.4835C20.0078 15.7764 19.533 15.7764 19.2401 15.4835L12.5226 8.76595C12.2297 8.47306 12.2297 7.99818 12.5226 7.70529L14.4671 5.76075C16.4352 3.79268 19.5237 3.62792 21.6793 5.26646L24.7238 2.22166C25.0167 1.92875 25.4916 1.92873 25.7845 2.22161C26.0774 2.51449 26.0774 2.98936 25.7845 3.28227L22.7399 6.32717ZM19.7704 13.8925L21.1846 12.4783C22.7467 10.9162 22.7467 8.3835 21.1846 6.82141C19.6225 5.25931 17.0899 5.25931 15.5278 6.82141L14.1135 8.23562L19.7704 13.8925Z" fill="#212121"></path> <path d="M12.7778 11.215C13.0707 11.5079 13.0707 11.9828 12.7778 12.2757L10.6514 14.402L13.5982 17.3489L15.7238 15.2234C16.0167 14.9305 16.4916 14.9305 16.7844 15.2234C17.0773 15.5163 17.0773 15.9912 16.7844 16.284L14.6589 18.4095L15.4858 19.2364C15.7787 19.5293 15.7787 20.0042 15.4858 20.2971L13.5412 22.2416C11.5732 24.2096 8.48484 24.3745 6.32918 22.7361L3.28475 25.7808C2.99187 26.0737 2.517 26.0737 2.22409 25.7808C1.93118 25.488 1.93116 25.0131 2.22404 24.7202L5.26853 21.6754C3.63025 19.5197 3.79509 16.4314 5.76306 14.4635L7.7076 12.5189C8.0005 12.226 8.47537 12.226 8.76826 12.5189L9.59072 13.3414L11.7172 11.215C12.0101 10.9221 12.485 10.9221 12.7778 11.215ZM6.83028 21.1875C8.3929 22.7431 10.9207 22.7409 12.4806 21.181L13.8948 19.7668L8.23793 14.1099L6.82372 15.5241C5.26383 17.084 5.26163 19.6117 6.81709 21.1743L6.82366 21.1808L6.83028 21.1875Z" fill="#212121"></path> </g></svg>                  <span>Wrong Number - Disconnected</span>
                </button>
                <button 
                  @click="updateLeadAction('Contacting', 'Not In, Call back in 4 hours.', '4h')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                <svg style="width: 18px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill="#000000" d="M14.9547098,7.98576084 L15.0711,7.99552 C15.6179,8.07328 15.9981,8.57957 15.9204,9.12636 C15.6826,10.7983 14.9218,12.3522 13.747,13.5654 C12.5721,14.7785 11.0435,15.5888 9.37999,15.8801 C7.7165,16.1714 6.00349,15.9288 4.48631,15.187 C3.77335,14.8385 3.12082,14.3881 2.5472,13.8537 L1.70711,14.6938 C1.07714,15.3238 3.55271368e-15,14.8776 3.55271368e-15,13.9867 L3.55271368e-15,9.99998 L3.98673,9.99998 C4.87763,9.99998 5.3238,11.0771 4.69383,11.7071 L3.9626,12.4383 C4.38006,12.8181 4.85153,13.1394 5.36475,13.3903 C6.50264,13.9466 7.78739,14.1285 9.03501,13.9101 C10.2826,13.6916 11.4291,13.0839 12.3102,12.174 C13.1914,11.2641 13.762,10.0988 13.9403,8.84476 C14.0181,8.29798 14.5244,7.91776 15.0711,7.99552 L14.9547098,7.98576084 Z M11.5137,0.812976 C12.2279,1.16215 12.8814,1.61349 13.4558,2.14905 L14.2929,1.31193 C14.9229,0.681961 16,1.12813 16,2.01904 L16,6.00001 L12.019,6.00001 C11.1281,6.00001 10.6819,4.92287 11.3119,4.29291 L12.0404,3.56441 C11.6222,3.18346 11.1497,2.86125 10.6353,2.60973 C9.49736,2.05342 8.21261,1.87146 6.96499,2.08994 C5.71737,2.30841 4.57089,2.91611 3.68976,3.82599 C2.80862,4.73586 2.23802,5.90125 2.05969,7.15524 C1.98193,7.70202 1.47564,8.08224 0.928858,8.00448 C0.382075,7.92672 0.00185585,7.42043 0.0796146,6.87364 C0.31739,5.20166 1.07818,3.64782 2.25303,2.43465 C3.42788,1.22148 4.95652,0.411217 6.62001,0.119916 C8.2835,-0.171384 9.99651,0.0712178 11.5137,0.812976 Z"></path> </g></svg>                  
                <span>Not In - Retry 4 Hours</span>
                </button>
                <button 
                  @click="updateLeadAction('Contacting', 'Call Back Tomorrow', '24h')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                <svg style="width: 18px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M12 8V12L14.5 14.5" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" stroke="#000000" stroke-width="1.5" stroke-linecap="round"></path> </g></svg>                
                <span>Call Back Tomorrow</span>
                </button>
                <button 
                  @click="updateLeadAction('Contacting','Left Voicemail, call back tomorrow.', '24h')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  <span>Left Voicemail</span>
                </button>
                <button 
                  @click="updateLeadAction('Contacting','Call Back Next week','1w')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                <svg  height="18px" width="18px" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xml:space="preserve" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path class="st0" d="M118.612,89.298c9.483,0,17.177-7.686,17.177-17.168v-54.96C135.788,7.687,128.094,0,118.612,0 c-9.482,0-17.176,7.687-17.176,17.169v54.96C101.436,81.611,109.13,89.298,118.612,89.298z"></path> <path class="st0" d="M255.992,89.298c9.483,0,17.176-7.686,17.176-17.168v-54.96C273.169,7.687,265.475,0,255.992,0 c-9.483,0-17.169,7.687-17.169,17.169v54.96C238.824,81.611,246.51,89.298,255.992,89.298z"></path> <path class="st0" d="M393.372,89.298c9.49,0,17.176-7.686,17.176-17.168v-54.96C410.549,7.687,402.863,0,393.372,0 c-9.482,0-17.169,7.687-17.169,17.169v54.96C376.204,81.611,383.89,89.298,393.372,89.298z"></path> <path class="st0" d="M196.576,275.986h23.082c1.611,0,2.683-1.071,2.683-2.682v-20.931c0-1.079,0.539-1.611,1.611-1.611h61.142 v0.802l-59.578,151.904c-0.802,1.881,0,2.953,1.611,2.953h25.495c2.143,0,2.683-0.54,3.492-2.413l60.379-151.635 c0.54-1.341,0.81-2.683,0.81-4.834v-20.931c0-1.611-1.072-2.682-2.683-2.682H196.576c-1.611,0-2.682,1.071-2.682,2.682v46.696 C193.894,274.915,194.965,275.986,196.576,275.986z"></path> <path class="st0" d="M424.286,44.899v27.23c0,17.038-13.861,30.906-30.914,30.906c-17.038,0-30.906-13.869-30.906-30.906v-27.23 h-75.559v27.23c0,17.038-13.869,30.906-30.914,30.906c-17.038,0-30.907-13.869-30.907-30.906v-27.23h-75.558v27.23 c0,17.038-13.87,30.906-30.915,30.906c-17.038,0-30.914-13.869-30.914-30.906v-27.23H17.866V512h476.267V44.899H424.286z M450.86,468.735H61.132V143.116H450.86V468.735z"></path> </g> </g></svg>                
                <span>Call Back 1 Week</span>
                </button>
                <button 
                  @click="updateLeadAction('Contacting', 'Not Interested - Stocked Up, follow up in a week.', '1w')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <svg style="width: 18px" viewBox="0 0 17 17" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M12.66 16.179c0 0.312-0.009 0.312-0.009 0.312v0.009h-10.106c-0.313 0-0.313-0.009-0.313-0.009h-0.009v-6.572h1.081v5.519h8.286v-5.519h1.070v6.26zM10.509 14.356l-6.312 0.009v-1.348l6.312-0.009v1.348zM10.661 11.286l-0.116 1.339-6.295-0.58 0.125-1.339 6.286 0.58zM11.081 9.518l-0.349 1.305-6.098-1.635 0.348-1.304 6.099 1.634zM11.875 7.982l-0.688 1.16-5.437-3.213 0.687-1.161 5.438 3.214zM13.205 7.036l-1.106 0.759-3.564-5.214 1.116-0.769 3.554 5.224zM14.777 6.724l-1.331 0.232-1.080-6.224 1.33-0.232 1.081 6.224z" fill="#000000"></path> </g></svg>                  <span>Not Interested - Stocked Up</span>
                </button>
                <button 
                  @click="updateLeadAction('Pricing Shared', 'Interesting Pricing shared, follow up tomorrow.', '24h')" 
                  class="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                  <span>Interested - Pricing Shared</span>
                </button>
              </div>
            </div>
            
            <!-- Comments section -->
            <div class="p-6">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-sm font-medium text-gray-500">COMMENTS</h3>
                <span class="text-xs text-gray-500">{{ leadComments.length }} comments</span>
              </div>
              
              <div v-if="leadComments.length === 0" class="text-gray-400 text-sm italic mb-4">
                No comments yet
              </div>
              <div v-else class="space-y-4 mb-6 max-h-60 overflow-y-auto">
                <div 
                  v-for="(comment, index) in [...leadComments].reverse()" 
                  :key="index" 
                  class="bg-gray-50 rounded-lg p-3"
                >
                  <div class="flex justify-between items-start mb-2">
                    <div class="font-medium">{{ $store.getters['leads/usernameById'](comment.user_id) }}</div>
                    <div class="text-xs text-gray-500">{{ formatCommentDate(comment.created_at) }}</div>
                  </div>
                  <p class="text-sm text-gray-700">{{ comment.comment }}</p>
                </div>
              </div>
              
              <div class="mb-2">
                <textarea 
                  v-model="newComment" 
                  rows="2" 
                  class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Add a comment..."
                ></textarea>
              </div>
              <div class="flex justify-between items-center">
                <button 
                  @click="nextLead" 
                  :disabled="currentIndex >= filteredLeads.length - 1"
                  class="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Skip to next lead →
                </button>
                <button 
                  @click="addComment" 
                  class="px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors"
                  :disabled="!newComment.trim()"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
          
          <!-- Empty state -->
          <div v-else class="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
            <div class="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mx-auto"><path d="M17 6.1H3"></path><path d="M21 12.1H3"></path><path d="M15.1 18H3"></path></svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
            <p class="text-gray-500 mb-4">There are no leads matching your current filter criteria.</p>
            <button 
              @click="activeFilter = ''" 
              class="px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors"
            >
              View all leads
            </button>
          </div>
        </div>
      </div>
      
      <!-- Edit Lead Modal -->
      <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div class="p-6 border-b border-gray-200">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-bold text-gray-900">Edit Lead</h2>
              <button @click="showEditModal = false" class="text-gray-500 hover:text-gray-700">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
              </button>
            </div>
          </div>
          
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Basic Information -->
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input 
                    type="text" 
                    v-model="editForm.name"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input 
                    type="text" 
                    v-model="editForm.company"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    v-model="editForm.email"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input 
                    type="tel" 
                    v-model="editForm.phone"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
              
              <!-- Address and Status -->
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input 
                    type="text" 
                    v-model="editForm.street"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input 
                      type="text" 
                      v-model="editForm.city"
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input 
                      type="text" 
                      v-model="editForm.state"
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input 
                    type="text" 
                    v-model="editForm.zip"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select 
                    v-model="editForm.status"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option v-for="status in statusesList" :key="status" :value="status">
                      {{ status }}
                    </option>
                  </select>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                  <input 
                    type="date" 
                    v-model="editForm.follow_up_date"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div class="p-6 border-t border-gray-200 flex justify-end gap-3">
            <button 
              @click="showEditModal = false" 
              class="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              @click="saveEditForm" 
              class="px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
         <!-- Email Generator Slider -->
    <div 
      v-if="emailSliderOpen" 
      class="fixed inset-y-0 right-0 w-full sm:w-1/3 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-30 overflow-hidden flex flex-col"
      :class="{ 'translate-x-0': emailSliderOpen, 'translate-x-full': !emailSliderOpen }"
    >
      <div class="flex items-center justify-between p-4 border-b">
        <div>
          <h3 class="text-lg font-semibold">Generate Email</h3>
          <p class="text-sm text-gray-500">{{ currentLead?.name }}</p>
        </div>
        <button 
          @click="closeEmailSlider" 
          class="p-2 rounded-full hover:bg-gray-100"
        >
          <span class="text-xl">×</span>
        </button>
      </div>
      
      <div class="flex-1 overflow-y-auto p-4">
        <div class="mb-6">
          <label for="emailConsiderations" class="block text-sm font-medium text-gray-700 mb-2">Additional considerations</label>
          <textarea 
            id="emailConsiderations"
            v-model="emailConsiderations" 
            rows="5" 
            class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="Add any specific points you want to include in the email..."
          ></textarea>
        </div>
        
        <div v-if="generatedEmail.subject" class="mb-6 bg-gray-50 p-4 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700">GENERATED SUBJECT</h4>
            <button 
              @click="copyGeneratedEmailSubject" 
              class="text-sm bg-primary text-black hover:text-gray-900 px-2 py-1 rounded flex items-center gap-1"
            >
              <span v-if="emailCopiedSubject">Copied!</span>
              <span v-else>Copy</span>
            </button>
          </div>
          <div class="text-sm whitespace-pre-wrap">{{ generatedEmail.subject }}</div>
        </div>
        <div v-if="generatedEmail.body" class="mb-6 bg-gray-50 p-4 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700">GENERATED BODY</h4>
            <button 
              @click="copyGeneratedEmailBody" 
              class="text-sm bg-primary text-black hover:text-gray-900 px-2 py-1 rounded flex items-center gap-1"
            >
              <span v-if="emailCopiedBody">Copied!</span>
              <span v-else>Copy</span>
            </button>
          </div>
          <div class="text-sm whitespace-pre-wrap">{{ generatedEmail.body }}</div>
        </div>
      </div>
      
      <div class="p-4 border-t">
        <button 
          @click="generateEmail" 
          class="w-full px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          :disabled="isGenerating"
        >
          <span v-if="isGenerating" class="animate-spin">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          <span>{{ isGenerating ? 'Generating...' : 'Generate Email' }}</span>
        </button>
      </div>
    </div>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, computed, onMounted, watch } from 'vue';
  import { useStore } from 'vuex';
  import SideBar from '@/components/SideBar.vue';
  
  const store = useStore();
  
  // State
  const currentIndex = ref(0);
  const activeFilter = ref('');
  const stateFilter = ref('');
  const phoneIsCopied = ref(false);
  const newComment = ref('');
  const isEditingFollowUp = ref(false);
  const followUpDateDraft = ref('');
  const statusesList = ['New', 'Contacting','Voicemail', 'Inactive', 'Pricing Shared', 'Samples Sent', 'Ordered', 'Important'];
  const showEditModal = ref(false);
  const editForm = reactive({
    name: '',
    company: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    status: '',
    follow_up_date: '',
  });
  const commentsSliderOpen = ref(false);

    // Email generator state
  const emailSliderOpen = ref(false);
  const emailConsiderations = ref('');
  const generatedEmail = reactive({subject: '',body: ''});
  const isGenerating = ref(false);
  const emailCopiedSubject = ref(false);
  const emailCopiedBody = ref(false);

  function parseHours(meta) {
    try {
      const data = JSON.parse(meta || '{}');
      return data.operating_hours || [];
    } catch {
      return [];
    }
  }

  function isOpenNowFancy(hourRange, day, timezone = 'America/Los_Angeles') {
  if (!hourRange || !day) return false;

  const nowInTz = new Date(
    new Date().toLocaleString('en-US', { timeZone: timezone })
  );
  const nowDay = nowInTz
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase();
  if (day.toLowerCase() !== nowDay) return false;

  const nowMinutes = nowInTz.getHours() * 60 + nowInTz.getMinutes();

  const cleanedRange = hourRange
    .replace(/[–—‒−]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  // ensure we have an open–close pair
  const dashIndex = cleanedRange.indexOf('-');
  if (dashIndex < 0) return false;
  const openStr = cleanedRange.slice(0, dashIndex).trim();
  const closeStr = cleanedRange.slice(dashIndex + 1).trim();
  if (!openStr || !closeStr) return false;

  const toMinutes = str => {
    if (!str) return null;
    const m = str.match(/^(\d{1,2})(?::(\d{2}))?\s?(AM|PM)$/i);
    if (!m) return null;
    let h = +m[1],
      mns = +m[2] || 0;
    const period = m[3].toUpperCase();
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + mns;
  };

  const openMinutes = toMinutes(openStr);
  const closeMinutes = toMinutes(closeStr);
  if (openMinutes == null || closeMinutes == null) return false;

  // overnight wrap?
  if (closeMinutes <= openMinutes) {
    return nowMinutes >= openMinutes || nowMinutes <= closeMinutes;
  }
  // normal range
  return nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
}


  const getInitials = (name = '') => {
    return name
      .split(' ')
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

   const getAvatarColor = (id) =>  {
    const colors = [
      '#FFB3BA', // soft pink
      '#FFDFBA', // peach
      '#FFFFBA', // light yellow
      '#BAFFC9', // mint
      '#BAE1FF', // baby blue
      '#E5BAFF', // lavender
      '#D5F4E6', // pale aqua
      '#FFCCE5', // light rose
      '#C5FAD5', // seafoam
      '#FFF2CC'  // cream
    ];
      return colors[id % colors.length] || '#95a5a6';
    }
  // Computed properties
  const allLeads = computed(() => store.state.leads.all);

  const availableStates = computed(() => {
    const states = new Set();
    allLeads.value.forEach(lead => {
      if (lead.state) {
        const s = lead.state.trim().toUpperCase();
        if (s.length === 2) {
          states.add(s);
        }
      }
    });
    return Array.from(states).sort();
  });

  const toLocalMidnight = raw => {
    if (!raw) return null;
    // Grab only the date portion (in case it’s a full ISO)
    const datePart = raw.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day).getTime();
    };  

  const filteredLeads = computed(() => {
    let leads = [...allLeads.value];
    
    if (activeFilter.value === 'follow_up_today') {
      leads = leads.filter(lead => isToday(lead.follow_up_date));
    } else if (activeFilter.value === 'no_status_change') {
      leads = leads.filter(lead => !lead.last_contacted);
    } else if (activeFilter.value === 'new') {
      leads = leads.filter(lead => lead.status === 'New');
    }

    // State filter
    if (stateFilter.value) {
      leads = leads.filter(lead => lead.state && lead.state.toUpperCase() === stateFilter.value);
    }
    
    // Sort by priority: follow-up today, overdue, upcoming, then the rest
    if (activeFilter.value === 'smart_sort') {

    leads = leads.sort((a, b) => {
      // First priority: today's follow-ups
      if (isToday(a.follow_up_date) && !isToday(b.follow_up_date)) return -1;
      if (!isToday(a.follow_up_date) && isToday(b.follow_up_date)) return 1;
      
      // Second priority: overdue follow-ups
      if (isOverdue(a.follow_up_date) && !isOverdue(b.follow_up_date)) return -1;
      if (!isOverdue(a.follow_up_date) && isOverdue(b.follow_up_date)) return 1;
      
      // Third priority: upcoming follow-ups
      if (isUpcoming(a.follow_up_date) && !isUpcoming(b.follow_up_date)) return -1;
      if (!isUpcoming(a.follow_up_date) && isUpcoming(b.follow_up_date)) return 1;
      
      // Fourth priority: sort by follow-up date
      if (a.follow_up_date && b.follow_up_date) {
        return new Date(a.follow_up_date) - new Date(b.follow_up_date);
      }
      
      // Leads with follow-up dates come before those without
      if (a.follow_up_date && !b.follow_up_date) return -1;
      if (!a.follow_up_date && b.follow_up_date) return 1;
      
      // Finally, sort by name
      return (a.name || '').localeCompare(b.name || '');
      });
    }

    return leads;
  });
  

  const currentLead = computed(() => {
    if (filteredLeads.value.length === 0) return null;
    return filteredLeads.value[currentIndex.value];
  });
  
  const leadComments = computed(() => 
    currentLead.value ? store.getters['leads/commentsFor'](currentLead.value.id) : []
  );
  
  
  // Date helper functions
  const today = computed(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  });
  
    const isOverdue = date =>
    date && toLocalMidnight(date) < today.value;

    const isToday = date =>
    date && toLocalMidnight(date) === today.value;

    const isUpcoming = date => {
    if (!date) return false;
    const t = toLocalMidnight(date);
    return t > today.value && t <= today.value + 7 * 24 * 60 * 60 * 1000;
    };
  
    // after toLocalMidnight and today…
    const formatFollowUpDateLong = (date) => {
  if (!date) return '';

  const followUpDate = new Date(date); // assume UTC ISO from server
  const now = new Date(); // use browser's local time
  const diffTime = followUpDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const options = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  const formattedDateTime = followUpDate.toLocaleString(undefined, options); // browser auto uses its own tz

  if (diffDays < 0) {
    return `Overdue: ${formattedDateTime} (${Math.abs(diffDays)} days ago)`;
  } else if (diffDays === 0) {
    return `Today: ${formattedDateTime}`;
  } else if (diffDays === 1) {
    return `Tomorrow: ${formattedDateTime}`;
  } else {
    return `${formattedDateTime} (in ${diffDays} days)`;
  }
};

  
  const formatCommentDate = (date) => {
    return new Date(date).toLocaleString();
  };
  
  const formatCityStateZip = (lead) => {
    const parts = [];
    if (lead.city) parts.push(lead.city);
    if (lead.state) parts.push(lead.state);
    if (lead.zip) parts.push(lead.zip);
    
    return parts.length > 0 ? parts.join(', ') : '';
  };
  
  // Methods
  const nextLead = () => {
    if (currentIndex.value < filteredLeads.value.length - 1) {
      currentIndex.value++;
      loadCurrentLeadComments();
    }
  };
  
  const previousLead = () => {
    if (currentIndex.value > 0) {
      currentIndex.value--;
      loadCurrentLeadComments();
    }
  };
  
  const loadCurrentLeadComments = () => {
    if (currentLead.value) {
      store.dispatch('leads/fetchComments', currentLead.value.id);
    }
  };
  
  const copyPhone = (phone) => {
    if (!phone) return;
    
    navigator.clipboard.writeText(phone).then(() => {
      phoneIsCopied.value = true;
      setTimeout(() => {
        phoneIsCopied.value = false;
      }, 2000);
    });
  };
  
  const updateLeadStatus = async (status) => {
    if (currentLead.value && currentLead.value.status !== status) {
      await updateLead({ status });
    }
  };
  
  const updateLeadAction = async (status, comment, followUpType = null) => {
  if (!currentLead.value) return;

  // Add comment
  if (comment) {
    await store.dispatch('leads/addComment', {
      leadId: currentLead.value.id,
      text: comment
    });
  }

  // Prepare updates
  const updates = {};
  if (status) updates.status = status;

  if (followUpType) {
    const now = new Date();
    let addHours = 0;
    if (followUpType === '4h')      addHours = 4;
    else if (followUpType === '24h') addHours = 24;
    else if (followUpType === '1w')  addHours = 24 * 7;

    // Add in UTC so it’s consistent everywhere
    now.setUTCHours(now.getUTCHours() + addHours);
    updates.follow_up_date = now.toISOString(); 
  }

  // Update lead
  if (Object.keys(updates).length > 0) {
    await updateLead(updates);
  }
};
 





  // Email generator functions
  function openEmailSlider(lead) {
    currentLead.value = lead;
    emailSliderOpen.value = true;
    emailConsiderations.value = '';
    generatedEmail.body = '';
    generatedEmail.subject = '';
    emailCopiedSubject.value = false;
    emailCopiedBody.value = false;

  }
  
  function closeEmailSlider() {
    emailSliderOpen.value = false;
    setTimeout(() => {
      if (!commentsSliderOpen.value) {
        currentLead.value = null;
      }
    }, 300); // Wait for transition to complete
  }
  
  async function generateEmail() {
  if (!currentLead.value) return;

  isGenerating.value = true;
  generatedEmail.body = '';
  generatedEmail.subject = '';

  try {
    const response = await store.dispatch('leads/generateEmail', {
      lead: currentLead.value,
      considerations: emailConsiderations.value
    });
    generatedEmail.subject = response.subject;
    generatedEmail.body = response.body;
  } catch (error) {
    console.error('Error generating email:', error);
  } finally {
    isGenerating.value = false;
  }
}
  function copyGeneratedEmailSubject() {
      if (!generatedEmail.subject) return;
      
      navigator.clipboard.writeText(generatedEmail.subject).then(() => {
        emailCopiedSubject.value = true;
        setTimeout(() => {
          emailCopiedSubject.value = false;
        }, 2000);
      });
    }
  
  function copyGeneratedEmailBody() {
    if (!generatedEmail.body) return;
    
    navigator.clipboard.writeText(generatedEmail.body).then(() => {
      emailCopiedBody.value = true;
      setTimeout(() => {
        emailCopiedBody.value = false;
      }, 2000);
    });
  }

  const addComment = async () => {
    if (!currentLead.value || !newComment.value.trim()) return;
    
    try {
      // Make sure we're using the API to persist comments
      await store.dispatch('leads/addComment', {
        leadId: currentLead.value.id,
        text: newComment.value.trim()
      });
      
      // Explicitly fetch comments again to ensure they're updated
      await store.dispatch('leads/fetchComments', currentLead.value.id);
      
      newComment.value = '';
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };
  
  // Follow-up functions
  const startEditFollowUp = () => {
    followUpDateDraft.value = currentLead.value?.follow_up_date || '';
    isEditingFollowUp.value = true;
  };
  
  const cancelEditFollowUp = () => {
    isEditingFollowUp.value = false;
    followUpDateDraft.value = '';
  };
  
  async function saveFollowUpDate() {
  if (!currentLead.value || !followUpDateDraft.value) return;

  // Convert local datetime from picker to proper UTC string
  const localDate = new Date(followUpDateDraft.value); // already local
  const isoUTC = localDate.toISOString(); // safely shifts to UTC

  // Send to backend
  const updated = await store.dispatch('leads/updateLead', {
    id: currentLead.value.id,
    updates: { follow_up_date: isoUTC }
  });

  // Update frontend
  currentLead.value.follow_up_date = updated.follow_up_date;
  isEditingFollowUp.value = false;
}
  
  const clearFollowUpDate = async () => {
    if (currentLead.value) {
      await updateLead({ follow_up_date: null });
    }
  };
  
  const updateLead = async (updates) => {
    if (!currentLead.value) return;
    
    try {
      await store.dispatch('leads/updateLead', {
        id: currentLead.value.id,
        updates
      });
    } catch (err) {
      console.error('Lead update failed', err);
    }
  };
  
  const getStatusClass = (status) => {
    const classes = {
      'New': 'bg-lime-100 text-lime-800',
      'Contacting': 'bg-green-100 text-green-800',
      'Voicemail': 'bg-teal-100 text-teal-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Pricing Shared': 'bg-blue-100 text-blue-800',
      'Samples Sent': 'bg-purple-100 text-purple-800',
      'Ordered': 'bg-orange-100 text-orange-800',
      'Important': 'bg-red-100 text-red-800'

    };
    
    return classes[status] || 'bg-gray-100 text-gray-800';
  };
  
  const openEditModal = () => {
    if (!currentLead.value) return;
    
    // Copy current lead data to the form
    editForm.name = currentLead.value.name || '';
    editForm.company = currentLead.value.company || '';
    editForm.email = currentLead.value.email || '';
    editForm.phone = currentLead.value.phone || '';
    editForm.street = currentLead.value.street || '';
    editForm.city = currentLead.value.city || '';
    editForm.state = currentLead.value.state || '';
    editForm.zip = currentLead.value.zip || '';
    editForm.status = currentLead.value.status || '';
    editForm.follow_up_date = currentLead.value.follow_up_date || '';
    
    showEditModal.value = true;
  };
  
  const saveEditForm = async () => {
    if (!currentLead.value) return;
    
    try {
      await store.dispatch('leads/updateLead', {
        id: currentLead.value.id,
        updates: { ...editForm }
      });
      
      showEditModal.value = false;
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };
  
  // Watchers
  watch([activeFilter, stateFilter], () => {
    currentIndex.value = 0;
    loadCurrentLeadComments();
  });
  
  // Lifecycle hooks
  onMounted(async () => {
    store.dispatch('leads/fetchUserMap');
    await store.dispatch('leads/fetchLeads');
    
    // Load comments for the first lead
    if (filteredLeads.value.length > 0) {
      loadCurrentLeadComments();
    }
  });
  </script>
  
  <style>
  :root {
    --color-primary: #6366f1;
    --color-primary-hover: #4f46e5;
  }
  
  .bg-primary {
    background-color: var(--color-primary);
  }
  
  
  .text-primary {
    color: var(--color-primary);
  }
  
  .border-primary {
    border-color: var(--color-primary);
  }
  
  .hover\:bg-primary\/90:hover {
    background-color: var(--color-primary-hover);
  }
  
  .focus\:ring-primary\/20:focus {
    --tw-ring-color: color-mix(in srgb, var(--color-primary) 20%, transparent);
  }
  
  .focus\:border-primary:focus {
    border-color: var(--color-primary);
  }
  </style>